import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import { Close } from '@material-ui/icons'
import { Box, Grid, IconButton, Modal, Paper, Tooltip, withStyles } from '@material-ui/core'

import { CleanButton, Label, SelectField, SelectList, Loader, Alert, PrimaryButton } from '../default'
import { acClearAddedProducts, acGetProductTables, acGetProducts, acPostAddProductToProductTable } from '../../class/actions'

function InsertProductModal({
  classes, open, onClose,
  loading, fetchError,
  productTables, productTablesError, loadingProductTables,
  products, loadingProducts
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [selectedProduct, setSelectedProduct] = useState('')
  const [selectedProductError, setSelectedProductError] = useState(false)
  const [error, setError] = useState(null)
  const [productTablesIncluded, setProductTablesIncluded] = useState([null])

  useEffect(() => {
    dispatch(acGetProductTables())
    dispatch(acClearAddedProducts())
  }, [dispatch])

  useEffect(() => {
    if (!products)
      dispatch(acGetProducts())
  }, [dispatch, products])

  const insertProducts = () => {
    if (!selectedProduct) {
      setSelectedProductError(true)
      return
    }

    if (productTablesIncluded.filter(productTable => productTable !== null).length && productTablesIncluded.length > new Set(productTablesIncluded.map(productTable => productTable ? productTable.product_table_id : -1)).size) {
      setError(t('modals.products.errorRepeatedProductTables'))
      return
    }

    setError(null)

    Promise.all(productTablesIncluded.filter(productTable => productTable !== null).map(newProductTable => {
      return new Promise((resolve, reject) => {
        dispatch(acPostAddProductToProductTable(newProductTable.product_table_id, selectedProduct, newProductTable.price))
          .then(value => resolve(value))
          .catch(err => reject(err))
      })
    }))
      .then(() => onClose())
      .catch(err => {
        console.log(err)
        setError(t('modals.products.unknownError'))
      })
  }

  const handleSelectListChange = data => {
    setProductTablesIncluded(data)
  }

  return (
    <Modal open={open} onClose={onClose} disableAutoFocus style={{ outline: 0, overflowY: 'scroll' }}>
      <Paper
        square={true}
        className={classes.root + ' ' + classes.modalDiv}
      >
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          {
            (loadingProductTables || loadingProducts || !products) ?
              <Loader /> : (
                <Grid container spacing={2} className={classes.grid}>

                  <Grid item xs={8}>
                    <Label v='h6'>
                      {t('modals.products.insert.title')}
                    </Label>
                  </Grid>
                  <Grid item xs={4} style={{ textAlign: 'right', padding: 0 }}>
                    <Tooltip title={t('default.close')}>
                      <IconButton onClick={onClose}>
                        <Close />
                      </IconButton>
                    </Tooltip>
                  </Grid>

                  <Grid item xs={7}>
                    <SelectField
                      label={t('modals.products.insert.product')}
                      items={products.map(product => ({
                        name: product.name,
                        value: product.product_id
                      }))}
                      value={selectedProduct}
                      onChange={e => {
                        setSelectedProduct(e.target.value)
                        setSelectedProductError(false)
                      }}
                      error={selectedProductError}
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Label bold>
                          {t('modals.products.addToProductTables')}
                        </Label>
                      </Grid>
                      <Grid item xs={12}>
                        <SelectList
                          label={t('modals.products.productTable')}
                          selectItems={productTables.map(productTable => ({
                            name: productTable.name,
                            value: productTable.product_table_id
                          }))}
                          priceLabel={t('modals.products.price')}
                          extraDataKey='price'
                          valueKey='product_table_id'
                          extraDataDefaultValue={0}
                          extraDataParseFunction={value => parseFloat(value)}
                          onChange={handleSelectListChange}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )
          }
        </Box>

        <div>
          <div style={{ marginTop: '15px' }}>
            {
              (fetchError) ? (
                <Alert style={{ marginBottom: '15px' }} noMargin color="error" text={fetchError.description ? fetchError.description : t('modals.products.unknownFetchError')} />
              )
                : (productTablesError) ? (
                  <Alert style={{ marginBottom: '15px' }} noMargin color="error" text={productTablesError.description ? productTablesError.description : t('modals.products.unknownProductTableError')} />
                )
                  : error ?
                    <Alert style={{ marginBottom: '15px' }} noMargin color="error" text={error} /> : null
            }
          </div>
          <div style={{ marginTop: '15px' }}>
            <Grid container xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <CleanButton onClick={onClose} style={{ marginRight: '15px' }}>
                {t('default.cancel')}
              </CleanButton>
              <PrimaryButton onClick={insertProducts}>
                {
                  (loading || loadingProductTables) ? (
                    <Loader />
                  ) : (
                    <div>
                      {t('modals.products.insertProduct')}
                    </div>
                  )
                }
              </PrimaryButton>
            </Grid>
          </div>
        </div>
      </Paper>
    </Modal>
  )
}

InsertProductModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  close: PropTypes.func
}

InsertProductModal.defaultProps = {
  open: false,
  onClose: () => true,
  close: () => true
}

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    padding: theme.spacing(2),
  },
  grid: {
    display: 'flex',
    flex: 1
  },
  modalDiv: {
    [theme.breakpoints.up('md')]: {
      width: '50%'
    },
    width: '100%',
    top: '10%',
    left: '50%',
    transform: 'translate(-50%, -10%)',
  }
})

const mapStateToProps = ({ ReducerFinancial, ReducerProductTable, ReducerProduct }) => ({
  loading: ReducerFinancial.loading,
  fetchError: ReducerFinancial.error,
  payment: ReducerFinancial.payment,

  productTables: ReducerProductTable.productTables,
  productTablesError: ReducerProductTable.productTablesError,
  loadingProductTables: ReducerProductTable.loadingProductTables,

  products: ReducerProduct.products,
  productsError: ReducerProduct.productsError,
  loadingProducts: ReducerProduct.loadingProducts
})

export default connect(mapStateToProps)(withStyles(styles)(InsertProductModal))